import { useEffect, useRef, useState } from 'react';
import { X, MapPin, Search } from 'lucide-react';
import { parseUbicacion } from '@shared/ubicacionParser';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';
import { faltaElNumeroEnElResultado, numeroDeCasa } from './direccionEncontrada';
import L from 'leaflet';
import iconUrl from 'leaflet/dist/images/marker-icon.png';
import iconRetinaUrl from 'leaflet/dist/images/marker-icon-2x.png';
import shadowUrl from 'leaflet/dist/images/marker-shadow.png';
import 'leaflet/dist/leaflet.css';

// Why: en bundlers (Vite) el image-resolution interno de Leaflet falla y los
// markers no aparecen. Forzamos el icono con assets importados.
const defaultIcon = L.icon({
  iconUrl,
  iconRetinaUrl,
  shadowUrl,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});
L.Marker.prototype.options.icon = defaultIcon;

/**
 * Selector de ubicación con OpenStreetMap + Leaflet.
 *
 * Why: Compromiso #2 (cero licenciamiento comercial) descarta Google Maps.
 * Stack:
 *   - leaflet (BSD-2-Clause)
 *   - tiles OSM (ODbL — atribución obligatoria)
 *   - geocoding Nominatim (1 req/s — debouncear)
 *
 * No usamos react-leaflet (Hippocratic License — incompatible con OSI).
 */

interface Props {
  latInicial?: number;
  lngInicial?: number;
  direccionInicial?: string;
  onConfirmar: (lat: number, lng: number, direccion?: string) => void;
  onCerrar: () => void;
}

// Villahermosa, Centro, Tabasco — fallback por mercado del cliente alpha.
const FALLBACK_LAT = 17.9892;
const FALLBACK_LNG = -92.9281;

// Sesgo de búsqueda Nominatim al estado de Tabasco (viewbox + MX).
// Why: la dueña casi siempre busca calles locales; sin viewbox Nominatim
// devuelve homónimos de otros estados primero.
const NOMINATIM_VIEWBOX = '-94.13,18.65,-91.00,17.25'; // left,top,right,bottom
const NOMINATIM_COUNTRY = 'mx';

interface NominatimItem {
  display_name: string;
  lat: string;
  lon: string;
}

export function MapaPicker({
  latInicial,
  lngInicial,
  direccionInicial,
  onConfirmar,
  onCerrar,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  const [lat, setLat] = useState(latInicial ?? FALLBACK_LAT);
  const [lng, setLng] = useState(lngInicial ?? FALLBACK_LNG);
  const [direccion, setDireccion] = useState(direccionInicial ?? '');
  const [busqueda, setBusqueda] = useState(direccionInicial ?? '');
  const [resultados, setResultados] = useState<NominatimItem[]>([]);
  const [buscando, setBuscando] = useState(false);
  const [errorBusqueda, setErrorBusqueda] = useState<string | null>(null);
  // Sprint 29 — pegar la ubicación que el cliente manda por WhatsApp.
  const [textoUbicacion, setTextoUbicacion] = useState('');
  const [resolviendo, setResolviendo] = useState(false);
  /**
   * Sprint 98 — ¿La ubicación que se va a guardar salió de elegir un resultado del buscador?
   *
   * Importa para poder avisar de lo que el mapa **no** encontró. Si el operador movió el marcador a
   * mano, ya vio dónde lo puso y el aviso sobraría.
   */
  const [elegidaDelBuscador, setElegidaDelBuscador] = useState(false);

  // Inicializa el mapa al montar.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = L.map(containerRef.current, {
      center: [lat, lng],
      zoom: latInicial ? 17 : 13,
      zoomControl: true,
      scrollWheelZoom: true,
      doubleClickZoom: true,
      touchZoom: true,
    });

    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      // Atribución obligatoria por la licencia ODbL.
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(map);

    const marker = L.marker([lat, lng], { draggable: true }).addTo(map);
    marker.on('dragend', () => {
      const ll = marker.getLatLng();
      setLat(ll.lat);
      setLng(ll.lng);
      reverseGeocode(ll.lat, ll.lng);
    });

    map.on('click', (e: L.LeafletMouseEvent) => {
      marker.setLatLng(e.latlng);
      setLat(e.latlng.lat);
      setLng(e.latlng.lng);
      reverseGeocode(e.latlng.lat, e.latlng.lng);
    });

    mapRef.current = map;
    markerRef.current = marker;

    // Why: el modal monta antes de que el flex padre calcule altura final.
    // Sin invalidateSize, Leaflet cree que el contenedor mide 0px, los tiles
    // no se piden y los handlers de wheel/click responden con offsets erróneos.
    requestAnimationFrame(() => {
      map.invalidateSize();
    });
    setTimeout(() => map.invalidateSize(), 250);

    return () => {
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reverse geocoding (Nominatim — 1 req/s, sin auth).
  const reverseGeocode = async (la: number, lo: number) => {
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${la}&lon=${lo}`,
        { headers: { 'Accept-Language': 'es' } },
      );
      const j = (await res.json()) as { display_name?: string };
      if (j.display_name) setDireccion(j.display_name);
    } catch (e) {
      console.warn('[MapaPicker] reverseGeocode falló:', e);
    }
  };

  const buscar = async () => {
    if (busqueda.trim().length < 3) return;
    setBuscando(true);
    setErrorBusqueda(null);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=jsonv2&q=${encodeURIComponent(busqueda)}&limit=5&countrycodes=${NOMINATIM_COUNTRY}&viewbox=${NOMINATIM_VIEWBOX}&bounded=0`,
        { headers: { 'Accept-Language': 'es' } },
      );
      if (!res.ok) {
        throw new Error(`Nominatim HTTP ${res.status}`);
      }
      const j = (await res.json()) as NominatimItem[];
      setResultados(j);
      if (j.length === 0) setErrorBusqueda('Sin resultados para esa búsqueda.');
    } catch (e) {
      console.error('[MapaPicker] búsqueda falló:', e);
      setResultados([]);
      setErrorBusqueda(
        e instanceof Error
          ? `No se pudo buscar (${e.message}). Verifica conexión a internet.`
          : 'No se pudo buscar. Verifica conexión a internet.',
      );
    } finally {
      setBuscando(false);
    }
  };

  const seleccionarResultado = (it: NominatimItem) => {
    const la = Number(it.lat);
    const lo = Number(it.lon);
    setLat(la);
    setLng(lo);
    setDireccion(it.display_name);
    if (mapRef.current && markerRef.current) {
      mapRef.current.setView([la, lo], 17);
      markerRef.current.setLatLng([la, lo]);
    }
    setResultados([]);
    setElegidaDelBuscador(true);
  };

  /**
   * El número de la casa que se buscó y que el mapa NO devolvió.
   *
   * Reporte del negocio con una captura: se buscó *"Calle Júpiter **206**, Fraccionamiento
   * Galaxia"* y el mapa ofreció *"Calle Jupiter, Fraccionamiento Galaxia, Villahermosa…"* —la calle
   * entera, sin número—. La lista no distinguía "esto es lo que encontré" de "esto es lo que vas a
   * usar", así que se confirmaba sin más y el repartidor salía con una calle, no con una casa.
   *
   * OpenStreetMap no tiene todos los números de Villahermosa y no los va a tener: lo que el sistema
   * puede hacer es **decirlo** y pedir el punto exacto, que es lo que el repartidor necesita de
   * todos modos.
   */
  const numeroBuscado = numeroDeCasa(busqueda);
  const faltaElNumero = elegidaDelBuscador && faltaElNumeroEnElResultado(busqueda, direccion);

  // Sprint 29 — mueve el marcador a unas coordenadas y reverse-geocodea.
  const moverMarcadorA = (la: number, lo: number) => {
    setLat(la);
    setLng(lo);
    // Ya no viene de la lista del buscador: el punto lo puso una persona o el enlace del cliente.
    setElegidaDelBuscador(false);
    if (mapRef.current && markerRef.current) {
      mapRef.current.setView([la, lo], 17);
      markerRef.current.setLatLng([la, lo]);
    }
    reverseGeocode(la, lo);
  };

  /**
   * Sprint 29 — Toma lo que la dueña pegó (enlace de Google Maps o coordenadas
   * que el cliente mandó por WhatsApp) y coloca el marcador. Los enlaces cortos
   * `goo.gl` se resuelven en el proceso main (siguen el redirect).
   */
  const aplicarUbicacionPegada = async () => {
    const r = parseUbicacion(textoUbicacion);
    if (r.tipo === 'no_reconocido') {
      setErrorBusqueda(
        'No reconocí una ubicación ahí. Pega el enlace de Google Maps que mandó el cliente, o las coordenadas (ej. 17.98, -92.92).',
      );
      return;
    }
    setErrorBusqueda(null);
    if (r.tipo === 'coords') {
      moverMarcadorA(r.lat, r.lng);
      setTextoUbicacion('');
      return;
    }
    // Enlace corto: el redirect lo sigue el proceso main.
    setResolviendo(true);
    const res = await window.api.ubicacion.resolverEnlace(r.url);
    setResolviendo(false);
    if (!res.ok) {
      setErrorBusqueda(traducirErrorBackend(res.error));
      return;
    }
    moverMarcadorA(res.data.lat, res.data.lng);
    setTextoUbicacion('');
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4">
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <div className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-blue-600" />
            <h2 className="font-semibold">Ubicar domicilio en el mapa</h2>
          </div>
          <button onClick={onCerrar} className="btn-ghost p-2" aria-label="Cerrar">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex items-center gap-2 border-b border-slate-200 bg-slate-50 px-5 py-2">
          <input
            className="input flex-1"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                buscar();
              }
            }}
            placeholder="Buscar dirección, calle, colonia..."
          />
          <button onClick={buscar} className="btn-secondary" disabled={buscando}>
            <Search className="h-4 w-4" />
            {buscando ? 'Buscando...' : 'Buscar'}
          </button>
        </div>

        {/* Sprint 29 — Pegar la ubicación que el cliente mandó por WhatsApp. */}
        <div className="flex items-center gap-2 border-b border-slate-200 bg-blue-50 px-5 py-2">
          <span className="shrink-0 text-xs font-semibold text-blue-700">📍 De WhatsApp</span>
          <input
            className="input flex-1"
            value={textoUbicacion}
            onChange={(e) => setTextoUbicacion(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                aplicarUbicacionPegada();
              }
            }}
            placeholder="Pega aquí el enlace de Google Maps o las coordenadas del cliente"
          />
          <button
            onClick={aplicarUbicacionPegada}
            className="btn-secondary"
            disabled={resolviendo || textoUbicacion.trim().length === 0}
          >
            <MapPin className="h-4 w-4" />
            {resolviendo ? 'Ubicando...' : 'Ubicar'}
          </button>
        </div>

        {errorBusqueda && (
          <div className="border-b border-rose-200 bg-rose-50 px-5 py-2 text-sm text-rose-700">
            {errorBusqueda}
          </div>
        )}

        {/*
          Sprint 98 — Los resultados se ven como lo que son: propuestas del mapa, sin elegir.

          Salían como renglones blancos sueltos, del mismo color que todo lo demás, y el negocio no
          podía distinguir *"esto encontré"* de *"esto vas a usar"*. Ahora la lista va en ámbar
          —color de "falta algo tuyo"— con el número de opciones y la instrucción en una línea. La
          ubicación ya elegida vive abajo, en verde: dos colores, dos significados.
        */}
        {resultados.length > 0 && (
          <div className="max-h-44 overflow-auto border-b-2 border-amber-300 bg-amber-50">
            <div className="sticky top-0 flex items-center gap-2 bg-amber-100 px-5 py-1.5 text-xs font-bold uppercase tracking-wide text-amber-900">
              <Search className="h-3.5 w-3.5" />
              {resultados.length === 1
                ? 'Encontré esto — tócalo si es'
                : `Encontré ${resultados.length} lugares — toca cuál es`}
            </div>
            {resultados.map((r, i) => (
              <button
                key={i}
                onClick={() => seleccionarResultado(r)}
                className="flex w-full items-start gap-2 border-b border-amber-200 px-5 py-2.5 text-left text-sm hover:bg-amber-100"
              >
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
                <span className="text-slate-800">{r.display_name}</span>
              </button>
            ))}
          </div>
        )}

        <div ref={containerRef} className="min-h-0 flex-1" style={{ minHeight: 200 }} />

        <div className="border-t border-slate-200 bg-slate-50 px-5 py-3 space-y-2">
          <div className="text-xs text-slate-600">
            Toca el mapa o arrastra el marcador para precisar la ubicación.
          </div>

          {/*
            Sprint 98 — Lo que se va a guardar, en verde y con nombre.

            Era un renglón gris de 12 px que empezaba con un pin, igual que los resultados de la
            búsqueda. Aquí se decide a dónde va el repartidor: merece decir "esto es", no susurrarlo.
          */}
          {direccion ? (
            <div className="rounded-lg border-2 border-emerald-300 bg-emerald-50 px-3 py-2">
              <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-emerald-800">
                <MapPin className="h-3.5 w-3.5" />
                Esta es la dirección que se va a guardar
              </div>
              <div className="mt-0.5 text-sm text-slate-800">{direccion}</div>
              <div className="mt-1 font-mono text-xs text-slate-500">
                Lat: {lat.toFixed(5)} · Lng: {lng.toFixed(5)}
              </div>
            </div>
          ) : (
            <div className="text-sm font-mono text-slate-700">
              Lat: {lat.toFixed(5)} · Lng: {lng.toFixed(5)}
            </div>
          )}

          {/*
            El aviso que faltaba: el mapa devolvió la calle, no la casa.

            No bloquea —nunca se bloquea una venta por un dato que el mapa no tiene—, pero dice qué
            hacer: mover el marcador. El punto exacto es lo que el repartidor necesita; el texto de
            la dirección es sólo para leerlo.
          */}
          {faltaElNumero && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              <span className="font-semibold">
                El mapa no conoce el número {numeroBuscado} de esa calle.
              </span>{' '}
              Te dejó en la calle, no en la casa: arrastra el marcador a la puerta antes de
              continuar, o pega la ubicación que mandó el cliente.
            </div>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onCerrar} className="btn-ghost">
              Cancelar
            </button>
            <button
              onClick={() => onConfirmar(lat, lng, direccion || undefined)}
              className="btn-primary"
            >
              Usar esta ubicación
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

